import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { v4 as uuid } from 'uuid';

import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRowId, GridRowModes, GridRowModesModel } from '@mui/x-data-grid';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import HeaderInfo from '~/components/Common/HeaderInfo';
import DebugModal from '~/components/DebugModal/DebugModal';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';
import { getProductTypeEligibilityHelperTitle } from '~/components/PromoV3/ProductTypeEligibilityHelper';
import { RewardAmount } from '~/components/Vouchers/RewardAmountCell';

import { getMinSpendProductTypeOptions, getProductTypeOptions } from '~/services/PromoService';

import getAllCurrencyCodes from '~/utils/getAllCurrencyCodes';

import { ProductMultiSelectEditCell } from './ProductMultiSelectEditCell';
import { NumberEditCell, getProductLabel } from './VoucherRewardEditCells';
import { copyVoucherReward, createVoucherReward } from './voucherRewardCopyUtils';

type VoucherReward = definitions['VoucherRewardV1'];
type VoucherCondition = definitions['VoucherConditionV1'];
type VoucherRewardKind = definitions['Voucher']['rewardKind'];

type Props = {
  rewardKind: VoucherRewardKind;
  rewards: Array<VoucherReward>;
  conditions?: Array<VoucherCondition>;
  onChange: (rewards: Array<VoucherReward>) => void;
  currencyCode?: string;
  disabled?: boolean;
  showAdvanceDescription?: boolean;
};

const defaultColumnsProps: Partial<GridColDef<VoucherReward>> = {
  editable: true,
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
  flex: 1,
};

export default function VoucherRewards({
  rewards,
  rewardKind,
  conditions = [],
  onChange,
  currencyCode = 'AUD',
  showAdvanceDescription,
  disabled = false,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { promoMeta, isPromoMetaLoading } = usePromoMeta();
  const promoProductTypes = promoMeta?.promo_product_types ?? [];

  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [newRewardIds, setNewRewardIds] = useState<Array<string>>([]);
  useEffect(() => {
    setNewRewardIds((prev) => prev.filter((id) => rewards.some((reward) => reward.id_voucher_reward === id)));
  }, [rewards]);

  const productLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    const options = getProductTypeOptions(promoProductTypes, []);
    options.forEach((opt) => map.set(opt.value, opt.label));
    return map;
  }, [promoProductTypes]);

  const minSpendProductLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    const options = getMinSpendProductTypeOptions(promoProductTypes, []);
    options.forEach((opt) => map.set(opt.value, opt.label));
    return map;
  }, [promoProductTypes]);

  const getDefaultProducts = useCallback(() => {
    const defaults = promoProductTypes.flatMap((category) => {
      const allProduct = category.products.find((product) => product.product_bk === 'ALL' && !product.dontShowInAdmin);
      if (!allProduct) {
        return [];
      }
      const productOption = getProductTypeOptions(promoProductTypes).find(
        (option) => option.category_bk === category.category_bk && option.product_bk === allProduct.product_bk,
      );
      return productOption ? [productOption.value] : [];
    });
    return defaults;
  }, [promoProductTypes]);

  const getDefaultMinSpendProducts = useCallback((): NonNullable<VoucherReward['min_spend_products']> => {
    return promoProductTypes.flatMap((category) => {
      if (!category.canUseForProductLevelMinSpend) {
        return [];
      }
      const allProduct = category.products.find(
        (product) => product.product_bk === 'ALL' && !product.dontShowInAdmin && product.canUseForProductLevelMinSpend,
      );
      if (!allProduct) {
        return [];
      }
      const productOption = getMinSpendProductTypeOptions(promoProductTypes).find(
        (option) => option.category_bk === category.category_bk && option.product_bk === allProduct.product_bk,
      );
      return productOption ? [productOption.value] : [];
    });
  }, [promoProductTypes]);

  const handleAddReward = useCallback(() => {
    const newId = uuid();
    const previousReward = rewards[rewards.length - 1];
    const defaultProducts =
      previousReward?.products && previousReward.products.length > 0 ? previousReward.products : getDefaultProducts();
    const defaultMinSpendProducts =
      previousReward?.min_spend_products && previousReward.min_spend_products.length > 0
        ? previousReward.min_spend_products
        : getDefaultMinSpendProducts();
    const defaultRewardAmount = Number.isFinite(previousReward?.reward_amount) ? previousReward.reward_amount : 50;
    onChange([
      ...rewards,
      createVoucherReward({
        previousReward,
        newId,
        defaultCurrencyCode: currencyCode as VoucherReward['currency_code'],
        defaultRewardAmount,
        defaultProducts,
        defaultMinSpend: 500,
        defaultMinSpendProducts,
      }),
    ]);
    setNewRewardIds((prev) => [...prev, newId]);
    setRowModesModel((prev) => ({ ...prev, [newId]: { mode: GridRowModes.Edit, fieldToFocus: 'reward_amount' } }));
  }, [currencyCode, getDefaultMinSpendProducts, getDefaultProducts, onChange, rewards]);

  const handleEditClick = useCallback((id: GridRowId) => {
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.Edit } }));
  }, []);

  const handleSaveClick = useCallback((id: GridRowId) => {
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View } }));
  }, []);

  const handleCancelClick = useCallback(
    (id: GridRowId) => {
      if (newRewardIds.includes(String(id))) {
        onChange(rewards.filter((reward) => reward.id_voucher_reward !== id));
        setNewRewardIds((prev) => prev.filter((rewardId) => rewardId !== id));
      }
      setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View, ignoreModifications: true } }));
    },
    [newRewardIds, onChange, rewards],
  );

  const handleDeleteReward = useCallback(
    (rewardId: string) => {
      const next = rewards.filter((reward) => reward.id_voucher_reward !== rewardId);
      onChange(next);
    },
    [onChange, rewards],
  );

  const handleCopyReward = useCallback(
    (reward: VoucherReward) => {
      const copiedReward = copyVoucherReward(reward);
      onChange([...rewards, copiedReward]);
      setNewRewardIds((prev) => [...prev, copiedReward.id_voucher_reward]);
      setRowModesModel((prev) => ({
        ...prev,
        [copiedReward.id_voucher_reward]: { mode: GridRowModes.Edit, fieldToFocus: 'reward_amount' },
      }));
    },
    [onChange, rewards],
  );

  const validateReward = useCallback(
    (reward: VoucherReward) => {
      if (!Number.isFinite(reward.reward_amount) || Number(reward.reward_amount) <= 0) {
        enqueueSnackbar('Reward amount must be greater than 0', { variant: 'error' });
        return false;
      }
      if (!reward.products || reward.products.length === 0) {
        enqueueSnackbar('At least one product must be selected for the reward', { variant: 'error' });
        return false;
      }
      return true;
    },
    [enqueueSnackbar],
  );

  const processRowUpdate = useCallback(
    (updatedRow: VoucherReward) => {
      if (!validateReward(updatedRow)) {
        throw new Error('Validation failed');
      }
      const next = rewards.map((reward) =>
        reward.id_voucher_reward === updatedRow.id_voucher_reward ? updatedRow : reward,
      );
      onChange(next);
      setNewRewardIds((prev) => prev.filter((id) => id !== updatedRow.id_voucher_reward));
      return updatedRow;
    },
    [onChange, rewards, validateReward],
  );

  const handleProcessRowUpdateError = useCallback(
    (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update reward', { variant: 'error' });
    },
    [enqueueSnackbar],
  );

  const columns = useMemo<Array<GridColDef<VoucherReward>>>(
    () => [
      {
        field: 'actions',
        headerName: '',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const isEditing = rowModesModel[params.id]?.mode === GridRowModes.Edit;
          return (
            <Stack direction="row" spacing={1}>
              {isEditing ? (
                <>
                  <IconButton
                    aria-label="Save reward"
                    size="small"
                    onClick={() => handleSaveClick(params.id)}
                    disabled={disabled}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="Cancel reward edit"
                    size="small"
                    onClick={() => handleCancelClick(params.id)}
                    disabled={disabled}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <IconButton
                    aria-label="Edit reward"
                    size="small"
                    onClick={() => handleEditClick(params.id)}
                    disabled={disabled}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <Tooltip title="Copy reward">
                    <span>
                      <IconButton
                        aria-label="Copy reward"
                        size="small"
                        onClick={() => handleCopyReward(params.row)}
                        disabled={disabled || isEditing}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton
                    aria-label="Delete reward"
                    size="small"
                    onClick={() => handleDeleteReward(params.row.id_voucher_reward)}
                    disabled={disabled}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'reward_id',
        headerName: 'ID',
        minWidth: 120,
        renderCell: (params) => params.row.id_voucher_reward,
        ...defaultColumnsProps,
        editable: false,
      },
      {
        field: 'reward_amount',
        headerName: 'Reward Amount',
        minWidth: 140,
        renderCell: (params) => (
          <RewardAmount
            rewardAmount={params.row.reward_amount}
            rewardKind={rewardKind}
            showUnknownAlert={false}
            component="span"
          />
        ),
        renderEditCell: (params) => <NumberEditCell {...params} />,
        ...defaultColumnsProps,
      },
      {
        field: 'currency_code',
        headerName: 'Currency',
        minWidth: 120,
        renderCell: (params) => params.row.currency_code || currencyCode,
        renderEditCell: (params) => (
          <TextField
            select
            size="small"
            fullWidth
            value={params.value ?? ''}
            disabled={disabled}
            onChange={(event) => {
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: event.target.value,
              });
            }}
          >
            {getAllCurrencyCodes().map((currencyCodeOption: string) => (
              <MenuItem key={currencyCodeOption} value={currencyCodeOption}>
                {currencyCodeOption}
              </MenuItem>
            ))}
          </TextField>
        ),
        ...defaultColumnsProps,
      },
      {
        field: 'products',
        headerName: 'Products',
        minWidth: 220,
        flex: 2,
        renderCell: (params) => {
          const values = params.row.products ?? [];
          if (!values.length) {
            return '-';
          }
          return values.map((value) => getProductLabel(value, productLabelMap)).join(', ');
        },
        renderEditCell: (params) => (
          <ProductMultiSelectEditCell
            {...params}
            disabled={false}
            promoProductTypes={promoProductTypes}
            multiSelectType="products"
          />
        ),
        ...defaultColumnsProps,
      },
      {
        field: 'min_spend',
        headerName: 'Min Spend',
        minWidth: 120,
        renderCell: (params) => (params.row.min_spend == null ? '-' : params.row.min_spend),
        renderEditCell: (params) => <NumberEditCell {...params} disabled={false} />,
        ...defaultColumnsProps,
      },
      {
        field: 'min_spend_products',
        headerName: 'Min Spend Products',
        minWidth: 220,
        flex: 2,
        renderHeader: () => (
          <HeaderInfo
            title={getProductTypeEligibilityHelperTitle(promoProductTypes, 'minSpend')}
            header="Min Spend Products"
            iconType="tick"
          />
        ),
        renderCell: (params) => {
          const values = params.row.min_spend_products ?? [];
          if (!values.length) {
            return '-';
          }
          return values.map((value) => getProductLabel(value, minSpendProductLabelMap)).join(', ');
        },
        renderEditCell: (params) => (
          <ProductMultiSelectEditCell
            {...params}
            promoProductTypes={promoProductTypes}
            multiSelectType="min_spend_products"
          />
        ),
        ...defaultColumnsProps,
      },
      {
        field: 'conditions',
        headerName: 'Conditions',
        minWidth: 160,
        renderCell: (params) => {
          const rewardId = params.row.id_voucher_reward;
          const rewardSpecific: Array<VoucherCondition> = [];
          const global: Array<VoucherCondition> = [];
          conditions.forEach((condition) => {
            const rewardIds = condition.voucher_reward_ids ?? [];
            if (rewardIds.length > 0 && rewardIds.includes(rewardId)) {
              rewardSpecific.push(condition);
            } else if (rewardIds.length === 0) {
              global.push(condition);
            }
          });
          const sorted = [...rewardSpecific, ...global];
          const entries: Array<[string, unknown]> = sorted.map((condition) => {
            const key = `${condition.condition_type} ${
              condition.condition_apply_type !== 'NONE' ? condition.condition_apply_type : ''
            } ${condition.condition_value} [${condition.id_voucher_condition}]`;
            return [key, condition];
          });
          if (params.row.min_spend != null) {
            entries.push([`${params.row.currency_code} Min Spend`, params.row.min_spend]);
          }
          const debugData = Object.fromEntries(entries);
          return (
            <DebugModal
              type="generic"
              title={`${sorted.length} condition${sorted.length !== 1 ? 's' : ''}`}
              data={debugData}
            />
          );
        },
        ...defaultColumnsProps,
        editable: false,
      },
      {
        field: 'debug',
        headerName: 'Debug',
        renderCell: (params) => <DebugModal type="generic" title="Debug" data={{ reward: params.row }} />,
        ...defaultColumnsProps,
        editable: false,
      },
    ],
    [
      conditions,
      disabled,
      handleCancelClick,
      handleCopyReward,
      handleDeleteReward,
      handleEditClick,
      handleSaveClick,
      minSpendProductLabelMap,
      productLabelMap,
      promoProductTypes,
      rowModesModel,
      currencyCode,
      rewardKind,
    ],
  );

  return (
    <Box>
      {showAdvanceDescription && (
        <Typography variant="body2" color="text.secondary" mb={2}>
          The amount the voucher is worth, and for which products
        </Typography>
      )}
      <DataGrid
        rows={rewards}
        columns={columns}
        initialState={{
          columns: {
            columnVisibilityModel: {
              reward_id: false,
            },
          },
        }}
        getRowId={(row) => row.id_voucher_reward}
        disableRowSelectionOnClick
        hideFooter
        loading={isPromoMetaLoading}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        sx={{ backgroundColor: 'background.paper' }}
      />
      <Box mt={2}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddReward}
          disabled={disabled || isPromoMetaLoading}
        >
          Add reward
        </Button>
      </Box>
    </Box>
  );
}
